import React, { FC } from 'react';
import { Product } from 'shared/types/product/Product';
import { SwiperOptions } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import Subtitle, { SubtitleProps } from 'components/commercetools-ui/atoms/subtitle';
import Typography from 'components/commercetools-ui/atoms/typography';
import Wrapper from 'components/HOC/wrapper';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, mediumDesktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import { Reference } from 'types/reference';
import Link from '../../content/link';
import Tile from '../product-tile';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { Configure, Hits, InfiniteHits, useHits } from 'react-instantsearch-hooks-web';
import { CARE, CONTURA, COSMO, HOMECARE, LUNA, OPAL, URBANEUNIVERSALACCESSORIES } from 'helpers/constants/collection-names';
import ProductTile from '../product-tile';
import { getRelatedProductsFilterQuery } from './filterDetails';
import { mapProduct, mapFrontasticProductToHits } from 'helpers/algolia/map-product';
// import { CARE_COLLECTION } from 'helpers/constants/category';
import { localeMapping } from 'helpers/constants/localeMapping';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface ProductSliderProps extends Partial<SwiperOptions> {
  products?: Product[];
  parentProductId: string;
  subCategoryToFetch: string;
  categoryToFetch: string;
  title: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
}
function shuffle(o:any) {
    for (
      let j, x, i = o.length;
      i;
      j = Math.floor(Math.random() * i), x = o[--i], o[i] = o[j], o[j] = x
    );
    return o;
  }
const transformItems = (items:any) => {
    return shuffle(items)
  };

const ProductSliderAlgolia: FC<ProductSliderProps> = ({
  products=[],
  parentProductId,
  subCategoryToFetch,
  categoryToFetch,
  title,
  subline,
  ctaLabel,
  ctaLink,
  onProductClick,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  clearDefaultWrapperStyles = false,
  disableProductQuickView = false,
  disableProductWishlistButton = false,
  disableProductVariants = false,
  innerArrows = true,
  solidArrows = true,
  breakpoints = {},
  classNames = {},
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isMobileSize] = useMediaQuery(mobile);
  const [isDesktopSize] = useMediaQuery(desktop);
  const titleClassName = useClassNames([
    'mb-12 text-20',
    classNames.title,
    {
      xs: 'md:text-32',
      sm: 'md:text-18 lg:text-32',
      lg: 'md:text-22 lg:text-42',
    }[titleVariant ?? 'lg'],
  ]);
  const currentURL = usePathname();
  const { locale, slug } = useParams();
  const localeKey = localeMapping[locale];
  
  const { hits } = useHits({transformItems});

  const getSearchQuery = () => {
      return "";      
  };
  const isProductDetailPage = isPdpUrl(currentURL);
  const isCollectionPage = currentURL.includes('/collection/') || currentURL.includes('/livewell/');
  const categorySlug = (slug as string).split('/').at(-1);
  const maxHitsPerPage = (isProductDetailPage || isCollectionPage) ? 12 : 48;

  const getFilterQuery = () => {   
    if (categorySlug === COSMO)
      return `searchableAttributes.range:"Cirrus" OR searchableAttributes.range:"Elegance" OR searchableAttributes.range:"Elegance II"`;
    if (categorySlug === HOMECARE)
        return `NOT variants.searchableAttributes.exclusive-product-${locale.toUpperCase()}:"BUNNINGS" AND NOT searchableAttributes.sub-brand:"LiveWell" AND (categoryKeys.${localeKey}:"Grab Rails" OR categoryKeys.${localeKey}:"Showers" OR categoryKeys.${localeKey}:"Mixer Taps")`;
    if (categorySlug === URBANEUNIVERSALACCESSORIES)
       return `NOT searchableAttributes.special-page-classification:"urbane-universal-accessories" AND categoryKeys.${localeKey}:"Bathroom Holders" OR categoryKeys.${localeKey}:"Shelves/Cabinets" OR categoryKeys.${localeKey}:"Soap Accessories" OR categoryKeys.${localeKey}:"Toilet Roll Accessories" OR categoryKeys.${localeKey}:"Towel Accessories"`;
    if (categorySlug === LUNA || categorySlug === CONTURA )
      return `NOT searchableAttributes.range: ${categorySlug} AND NOT categoryKeys.${localeKey}:"Care Sinks" AND NOT categoryKeys.${localeKey}:"Care Tapware" AND NOT categoryKeys.${localeKey}:"Care Showers" AND NOT categoryKeys.${localeKey}:"Care Toilets" AND NOT categoryKeys.${localeKey}:"Care Accessories" AND NOT categoryKeys.${localeKey}:"Care Basins" AND NOT searchableAttributes.range:opal AND NOT searchableAttributes.range:"Opal II Easy Height"`;
    if(categorySlug === OPAL)
        return `categoryKeys.${localeKey}:"Care Sinks" OR categoryKeys.${localeKey}:"Care Tapware" OR categoryKeys.${localeKey}:"Care Showers" OR categoryKeys.${localeKey}:"Care Toilets" OR categoryKeys.${localeKey}:"Care Accessories" OR categoryKeys.${localeKey}:"Care Basins" AND NOT searchableAttributes.range:opal AND NOT searchableAttributes.range:"Opal II Easy Height"`
    if (categorySlug === CARE)  return `searchableAttributes.range:opal`
    if (isProductDetailPage) return `categoryKeys.${localeKey}:"${subCategoryToFetch}" AND NOT key:"${parentProductId}"`
      return `NOT searchableAttributes.range: "${categorySlug} II" AND NOT categoryKeys.${localeKey}:"Care Sinks" AND NOT categoryKeys.${localeKey}:"Care Tapware" AND NOT categoryKeys.${localeKey}:"Care Showers" AND NOT categoryKeys.${localeKey}:"Care Toilets" AND NOT categoryKeys.${localeKey}:"Care Accessories" AND NOT categoryKeys.${localeKey}:"Care Basins" AND NOT searchableAttributes.range:opal AND NOT searchableAttributes.range:"Opal II Easy Height"`;
  };


  const ctProductsTileMapping = products?.map((product: Product, index: number) => mapFrontasticProductToHits(product, locale)).filter(e => e.productId !== parentProductId).sort((a: any, b: any) => {
    return (a.selectedVariant.attributes.IsNew === b.selectedVariant.attributes.IsNew) ? 0 : a.selectedVariant.attributes.IsNew ? -1 : 1;
  });

  const sortedHits = hits.sort((a: any, b: any) => {
    return (a.searchableAttributes.IsNew === b.searchableAttributes.IsNew) ? 0 : a.searchableAttributes.IsNew ? -1 : 1;
  });


  return (
    <div
      className={`pt-22 md:pt-22 pb-48 md:pb-48 lg:py-48 xl:py-70 2xl:py-70 3xl:py-103  ${
        isProductDetailPage
          ? 'lg:grid lg:grid-cols-12 pr-0  md:pb-1  bg-[#F3EDE9] w-screen relative left-full -translate-x-full'
          : 'bg-[#E5E9EB] px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80'
      }`}
    >
      <Configure query={getSearchQuery()} filters={getFilterQuery()} hitsPerPage={maxHitsPerPage} facets={['*']} maxValuesPerFacet={999} 
        attributesToHighlight={[]} // Disable highlighting
        attributesToSnippet={[]} // Disable snippetting
        attributesToRetrieve={[
          "name",
          "slug",
          "categoryKeys",
          "masterVariantID",
          "searchableAttributes.IsNew",
          "variants.id",
          "variants.key",
          "variants.sku",
          "variants.images",
          "variants.attributes.colour",
          "variants.attributes.item-code",
          "variants.attributes.item-name",
          "variants.prices",
          "variants.attributes.variant-name",
        ]} 
      />
      <div id="related-products" className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4 pt-26 pl-24 md:pl-28 lg:pl-30 xl:pl-0 2xl:pl-0 xl:py-60' : ''}>
        <div>
          <Typography
            id="related-products-title"
            className={`pb-32 text-32 font-medium text-primary-black xl:text-36 2xl:text-48  ${
              isProductDetailPage ? '' : ''
            } `}
            as="h3"
          >
            {title}
          </Typography>
        </div>
        <div className={isProductDetailPage ? '' : ''}>



        <div className="product-slider-container relative w-full">
            <Slider
              className="mb-48"
              slidesPerView={2.1}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              arrows={isTabletSize || isDesktopSize || isMiniTabletSize}
              innerArrows={innerArrows}
              solidArrows={solidArrows}
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50,  }}
              prevButtonStyles={{ top: '100%', left: 0,  }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{

                [tablet]: {
                  slidesPerView: isProductDetailPage ? 3.25 : 4,
                  slidesPerGroup: isProductDetailPage ? 3 : 4,
                  spaceBetween: 16,
                  ...(breakpoints[mediumDesktop] ?? {}),
                },

                [smallMobile]: {
                  slidesPerView: isProductDetailPage ? 1.2 : 1.2,
                  slidesPerGroup: 1,
                  spaceBetween: 16,
                  ...(breakpoints[smallMobile] ?? {}),
                },

                [mobile]: {
                  slidesPerView: isProductDetailPage ? 2.1 : 2.1,
                  slidesPerGroup: 2,
                  spaceBetween: 16,
                  ...(breakpoints[mobile] ?? {}),
                },
              }}

              {...props}
            >

              {sortedHits.map((hit: any, index: number) => (
                <div key={hit.id || hit.objectID} className="mb-48">
                  <ProductTile
                    key={index}
                    images={hit?.images}
                    product={mapProduct(hit, locale)}
                        globalView={'grid'}                      
                  />
                </div>
              ))}

            </Slider>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProductSliderAlgolia;
